import { useApolloClient } from '@apollo/client';
import { Button, IllustratedMessage, Progress, Stack } from '@eleveo/components-react';
import { FilterIcon, PlusIcon, SearchNoneIcon } from '@eleveo/icons-react';
import { FormApi } from 'final-form';
import { difference, equals, pathOr } from 'ramda';
import React, { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { RouteComponentProps } from 'react-router';

import { Permissions, useAuth } from '../../app/AuthorizationProvider';
import { Header } from '../../components/LayoutNext/Header';
import { Panel, Content, SidebarWrapper } from '../../components/LayoutNext/LayoutNext';
import { ActiveFilters as ActiveFiltersNew } from '../../components/LayoutNext/components/ActiveFilters';
import { Center } from '../../components/LayoutNext/components/Center';
import {
  acdQueueItems,
  channelTypeItems,
  getActiveFilterValue,
  historicalDataIntervalItems,
  timezoneItems,
} from '../../components/LayoutNext/components/CheckboxFilterTile';
import { ModalConfirmationNewStyle as ModalConfirmation } from '../../components/ModalConfirmationNewStyle';
import { useSidebar } from '../../components/layout/Sidebar';
import {
  CreateQueueMutationVariables,
  GetQueuesDocument,
  GetQueuesQuery,
  Queue,
  UpdateQueueMutationVariables,
  GetAgentsProductionSchedulesQuery,
  GetAgentsProductionSchedulesDocument,
  Agent,
  GetQueueDocument,
  GetQueueQuery,
  GetTimezonesUsedInQueuesDocument,
  Timezone,
  useCreateQueueMutation,
  useDeleteQueueMutation,
  useGetAcdQueuesQuery,
  useGetSkillsAllQuery,
  useUpdateQueueMutation,
  useBulkAssignSupervisedAgentsMutation,
  useGetTimezonesAllQuery,
  useGetTimezonesUsedInQueuesQuery,
  useGetQueuesQuery,
} from '../../graphql/components';
import { useFilter } from '../../utils/filter';
import { fullNameOfAgent } from '../../utils/helpers';
import { QueuesFilterSidebar, QueuesSidebarFilterState } from './QueuesFilterSidebar';
import { QueuesHeaderFilterType, QueuesHeaderSearchForm } from './QueuesHeaderSearchForm';
import { QueuesSidebar } from './QueuesSidebar';
import { QueuesTable } from './QueuesTable';
import { TITLE_TAG, CONTAINER_TAG, ADD_QUEUE_BUTTON_TAG, TOGGLE_FILTER_BUTTON_TAG } from './constants';
import { createQueueFromValues } from './queuesUtils';

import type { ActionRouteType } from '../../types';
import type { Values } from './types';

type ForceEditModal = {
  open: boolean;
  updateUsedQueue: boolean;
  form?: FormApi<Values>;
  values?: Values;
};

type UsedAgentsModal =
  | { state: 'ready' }
  | {
      state: 'open';
      form: FormApi<Values>;
      values: Values;
      agents: Agent[];
    }
  | {
      state: 'proceed';
      form: FormApi<Values>;
      values: Values;
    };

const initModal = {
  open: false,
  updateUsedQueue: false,
};

export const defaultQueueSidebarFilter: QueuesSidebarFilterState = {
  skills: [],
  acdQueues: [],
  channelTypes: [],
  historicalDataIntervals: [],
  timezones: [],
};

export const QueuesScene: React.FC<RouteComponentProps<ActionRouteType>> = () => {
  const { userHasPermissions, userId } = useAuth();
  const { openSidebar, closeSidebar, toggleSidebar, expandSidebar, shrinkSidebar, isSidebarOpen } = useSidebar();

  const { t } = useTranslation();

  const [isFilterSidebarOpen, setIsFilterSidebarOpen] = useState(false);

  const [queueToEdit, setQueueToEdit] = useState<Queue | null>(null);
  const [queueToDelete, setQueueToDelete] = useState<Queue | null>(null);
  const [mutationInProgress, setMutationInProgress] = useState(false);
  const [forceEditModal, setForceEditModal] = useState<ForceEditModal>(initModal);
  const [usedAgentsModal, setUsedAgentsModal] = useState<UsedAgentsModal>({ state: 'ready' });

  const { filter, setFilter, isFilterActive } = useFilter<QueuesHeaderFilterType>();
  const { filter: sidebarFilter, setFilter: setSidebarFilter } =
    useFilter<QueuesSidebarFilterState>(defaultQueueSidebarFilter);

  // Sidebar Filter Accordion open tiles state
  const [preOpenedItemIds, setPreOpenedItemIds] = useState<string[]>([]);

  const getQueuesComponentVariables = {
    ...filter,
    channelTypes: sidebarFilter.channelTypes.toString() || undefined,
    acdQueueIds: sidebarFilter.acdQueues.toString() || undefined,
    timezones: sidebarFilter.timezones.toString() || undefined,
    historicalDataIntervals: sidebarFilter.historicalDataIntervals.toString() || undefined,
    skillIds: sidebarFilter.skills.toString() || undefined,
  };

  const client = useApolloClient();
  const acdQueuesResult = useGetAcdQueuesQuery({ fetchPolicy: 'network-only' });
  const skillsResult = useGetSkillsAllQuery({ fetchPolicy: 'network-only' });
  const timezonesResult = useGetTimezonesAllQuery();
  const timezoneUsedInQueuesResult = useGetTimezonesUsedInQueuesQuery({
    // INFO: By PO, this is a temporary solution and may be changed in the future with UX improvements
    onCompleted: (data) => {
      const timezonesUsedInQueuesObj: Record<string, true> = {};

      data.queuesTimezones.forEach(({ zoneId }: Timezone) => {
        timezonesUsedInQueuesObj[zoneId] = true;
      });

      const newTimezonesFilterObj: string[] = [];

      // Check if active timezone filter is still among the usedTimezones
      sidebarFilter.timezones.forEach((timezone) => {
        if (timezonesUsedInQueuesObj[timezone]) {
          newTimezonesFilterObj.push(timezone);
        }
      });

      setSidebarFilter({
        ...sidebarFilter,
        timezones: newTimezonesFilterObj,
      });
    },
    notifyOnNetworkStatusChange: true, // Needed to run `onCompleted` after `refetch()`
  });
  const queuesResult = useGetQueuesQuery({ fetchPolicy: 'network-only', variables: getQueuesComponentVariables });
  const [createQueue] = useCreateQueueMutation();
  const [updateQueue] = useUpdateQueueMutation();
  const [deleteQueue] = useDeleteQueueMutation();
  const [bulkAssignSupervisedAgents] = useBulkAssignSupervisedAgentsMutation();

  const loading =
    queuesResult.loading ||
    acdQueuesResult.loading ||
    skillsResult.loading ||
    timezonesResult.loading ||
    timezoneUsedInQueuesResult.loading;
  const error =
    queuesResult.error ||
    acdQueuesResult.error ||
    skillsResult.error ||
    timezonesResult.error ||
    timezoneUsedInQueuesResult.error;

  const queues: Queue[] = queuesResult.data?.queues ?? [];

  const skipRendering = (loading && !queues.length) || error;

  useEffect(() => {
    if (usedAgentsModal.state === 'proceed') {
      const { values, form } = usedAgentsModal;
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      handleUpdate(values, form);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usedAgentsModal.state]);

  const handlePrepareCreateQueue = () => {
    if (!isSidebarOpen) {
      openSidebar();

      setQueueToEdit(null);
      setIsFilterSidebarOpen(false);
    } else if (isFilterSidebarOpen) {
      setIsFilterSidebarOpen(false);
    } else if (queueToEdit) {
      setQueueToEdit(null);
    } else {
      toggleSidebar();
    }

    shrinkSidebar();
  };

  const handlePrepareAssignQueue = (queue: Queue) => {
    setIsFilterSidebarOpen(false);

    openSidebar();
    expandSidebar();
    setQueueToEdit(queue);
  };

  const handlePrepareEditQueue = (queue: Queue) => {
    setIsFilterSidebarOpen(false);

    openSidebar();
    shrinkSidebar();
    setQueueToEdit(queue);
  };

  const toggleFilterSidebar = () => {
    setPreOpenedItemIds([]);

    if (isFilterSidebarOpen) {
      closeSidebar();
      setIsFilterSidebarOpen(false);
    } else {
      setIsFilterSidebarOpen(true);
      setQueueToEdit(null);
      shrinkSidebar();

      if (!isSidebarOpen) {
        openSidebar();
      }
    }
  };

  const handleOpenFilterSidebarItem = (preOpenItemId: string) => {
    setQueueToEdit(null);
    shrinkSidebar();

    setIsFilterSidebarOpen(true);
    setPreOpenedItemIds([preOpenItemId]);
    openSidebar();
  };

  const handleCancelFilterSidebar = () => {
    closeSidebar();
    setIsFilterSidebarOpen(false);

    setPreOpenedItemIds([]);
  };

  const handleShowDeleteModal = (queue: Queue) => setQueueToDelete(queue);

  const handleCloseDeleteModal = () => setQueueToDelete(null);

  const updateCachedQueueWithLatestData = async (id: string) => {
    // No try/catch wrap as fn is used already in try/catch block
    const refetchQuery = await client.query<GetQueueQuery>({
      query: GetQueueDocument,
      variables: { id },
      fetchPolicy: 'no-cache',
    });

    const {
      data: { queue: updatedQueue },
    } = refetchQuery;

    const data = client.cache.readQuery<GetQueuesQuery>({
      query: GetQueuesDocument,
      variables: getQueuesComponentVariables,
    });

    if (data?.queues && updatedQueue) {
      const newQueues = data.queues.map((queue) => {
        if (queue.id === updatedQueue.id) {
          return updatedQueue;
        }

        return queue;
      });

      client.cache.writeQuery<GetQueuesQuery>({
        query: GetQueuesDocument,
        variables: getQueuesComponentVariables,
        data: { queues: newQueues },
      });
    }
  };

  const handleCreate = async (values: Values, form: FormApi<Values>) => {
    const variables: CreateQueueMutationVariables = {
      queue: createQueueFromValues(values),
    };

    try {
      setMutationInProgress(true);

      const createQueueResponse = await createQueue({
        variables,
        refetchQueries: [
          {
            query: GetQueuesDocument,
            variables: getQueuesComponentVariables,
          },
          GetTimezonesUsedInQueuesDocument,
        ],
      });

      const assignedIdsState = form.getFieldState('assignedIds');

      if (assignedIdsState?.modified && createQueueResponse) {
        await bulkAssignSupervisedAgents({
          variables: {
            supervisorId: userId,
            data: {
              queueId: createQueueResponse.data!.createQueue!.id!,
              agentsIds: values.assignedIds!,
            },
          },
        });

        updateCachedQueueWithLatestData(createQueueResponse.data!.createQueue!.id!);
      }

      closeSidebar();
    } catch (e) {
      // `customFetch()` will display error message
    } finally {
      setMutationInProgress(false);
    }
  };

  const handleUpdate = async (values: Values, form: FormApi<Values>) => {
    const { id } = values;
    const { updateUsedQueue } = forceEditModal;

    const variables: UpdateQueueMutationVariables = {
      id,
      updateUsedQueue,
      queue: createQueueFromValues(values),
    };

    const assignedIdsState = form.getFieldState('assignedIds');
    const hasAssignedAgentsChanged = assignedIdsState?.modified;
    const initialAssignedAgentIds = assignedIdsState?.initial ?? [];

    try {
      setMutationInProgress(true);

      // Find whether agents unassigned from the queue are contributing in a production schedule
      if (hasAssignedAgentsChanged && usedAgentsModal.state === 'ready') {
        const unassignedAgentIds = difference(initialAssignedAgentIds, values.assignedIds!);

        if (unassignedAgentIds.length > 0) {
          const checkFutureProductionSchedulesResult = await client.query<GetAgentsProductionSchedulesQuery>({
            query: GetAgentsProductionSchedulesDocument,
            variables: {
              parameters: {
                agentIds: unassignedAgentIds,
                queueIds: [values.id],
                startDateTime: new Date().toISOString(),
              },
            },
          });

          const agents = checkFutureProductionSchedulesResult.data.agentsProductionSchedules
            .filter((data) => data!.productionSchedules.length > 0)
            .map((data) => data!.agent);

          // Some unassigned agents are used in a production schedule
          if (agents.length > 0) {
            setUsedAgentsModal({
              form,
              values,
              agents,
              state: 'open',
            });

            return;
          }
        }
      }

      await updateQueue({
        variables,
        refetchQueries: [GetTimezonesUsedInQueuesDocument],
      });

      if (hasAssignedAgentsChanged) {
        await bulkAssignSupervisedAgents({
          variables: {
            supervisorId: userId,
            data: {
              queueId: values.id,
              agentsIds: values.assignedIds!,
            },
          },
        });
      }
      // Re-fetch queue to check correct `used` flag, data-interval and editable flags from BE and update Queues in cache
      updateCachedQueueWithLatestData(values.id);

      closeSidebar();
      setQueueToEdit(null);
      setUsedAgentsModal({ state: 'ready' });
    } catch (e) {
      const errorCode = pathOr(null, ['networkError', 'result', 'errorCode'], e);

      // Changes of ACD Queues or Timezone in WFM Queue when given queue is used in Forecast drafts, user gets message
      if (errorCode === 'fe.qm.queue.update.cantUpdate.usedInForecast') {
        setForceEditModal({
          form,
          values,
          open: true,
          updateUsedQueue: true,
        });
      }

      // It is not possible to remove all ACD Queues -> setting values back to initial after submit
      if (errorCode === 'fe.qm.queue.update.cantUpdate.noAcdQueues') {
        form.change('acdQueueIds', queueToEdit?.acdQueueIds);
      }
    } finally {
      setMutationInProgress(false);
    }
  };

  const handleDelete = async () => {
    if (!queueToDelete) {
      return;
    }

    const { id, used } = queueToDelete;

    if (used) {
      return;
    }

    try {
      setMutationInProgress(true);

      await deleteQueue({
        variables: {
          id,
        },
        update: (cache) => {
          const data = cache.readQuery<GetQueuesQuery>({
            query: GetQueuesDocument,
            variables: getQueuesComponentVariables,
          });

          if (data && data.queues) {
            const newQueues = data.queues.filter((queue) => queue.id !== id);

            cache.writeQuery<GetQueuesQuery>({
              query: GetQueuesDocument,
              variables: getQueuesComponentVariables,
              data: { queues: newQueues },
            });
          }
        },
        refetchQueries: [GetTimezonesUsedInQueuesDocument],
      });

      if (queueToEdit && queueToEdit.id === id) {
        closeSidebar();
        setQueueToEdit(null);
      }
    } catch (e) {
      // `customFetch()` will display error message
    } finally {
      setQueueToDelete(null);
      setMutationInProgress(false);
    }
  };

  const handleSubmit = (values: Values, form: FormApi<Values>) => {
    if (queueToEdit) {
      handleUpdate(values, form);
    } else {
      handleCreate(values, form);
    }
  };

  const handleCancel = () => {
    closeSidebar();
    setQueueToEdit(null);
  };

  const handleForceEditModalSubmit = () => {
    handleUpdate(forceEditModal.values!, forceEditModal.form!);
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    handleForceEditModalClose();
  };

  const handleForceEditModalClose = () => {
    setForceEditModal(initModal);
  };

  const handleUsedAgentsModalSubmit = () => {
    if (usedAgentsModal.state === 'open') {
      setUsedAgentsModal((state) => {
        // Condition above is not enough for TypeScript to infer correct type here
        if (state.state === 'open') {
          return { form: state.form, state: 'proceed', values: state.values };
        }

        return state;
      });
    }
  };

  const handleUsedAgentsModalClose = () => {
    if (usedAgentsModal.state === 'open') {
      setUsedAgentsModal({ state: 'ready' });
    }
  };

  const onFilterSubmit = ({ name }: QueuesHeaderFilterType) => {
    const values = {
      name: name && name.trim(),
    };
    setFilter(values);
  };

  const ActiveFilters = () => (
    <ActiveFiltersNew
      config={[
        {
          dataTestId: 'queues-active-sidebar-filter-channelTypes',
          defaultValue: [],
          id: '1',
          key: 'channelTypes',
          name: t('queue.channelType.channelTypes'),
          value: getActiveFilterValue(channelTypeItems(t), sidebarFilter.channelTypes),
        },
        {
          dataTestId: 'queues-active-sidebar-filter-acdQueues',
          defaultValue: [],
          id: '2',
          key: 'acdQueues',
          name: t('queue.acdQueues'),
          value: getActiveFilterValue(acdQueueItems(t, acdQueuesResult), sidebarFilter.acdQueues),
        },
        {
          dataTestId: 'queues-active-sidebar-filter-timezones',
          defaultValue: [],
          id: '3',
          key: 'timezones',
          name: t('timezone.timezone', { count: 2 }),
          value: getActiveFilterValue(timezoneItems(timezoneUsedInQueuesResult), sidebarFilter.timezones),
        },
        {
          dataTestId: 'queues-active-sidebar-filter-dateIntervals',
          defaultValue: [],
          id: '4',
          key: 'historicalDataIntervals',
          name: t('queue.dataInterval', { count: 2 }),
          value: getActiveFilterValue(historicalDataIntervalItems(t), sidebarFilter.historicalDataIntervals),
        },
        {
          dataTestId: 'queues-active-sidebar-filter-skills',
          defaultValue: [],
          id: '5',
          key: 'skills',
          name: t('skill.skills'),
          value: getActiveFilterValue(skillsResult.data?.skills, sidebarFilter.skills),
        },
      ]}
      sidebarFilter={sidebarFilter}
      setSidebarFilter={setSidebarFilter}
      openFilterSidebarItem={handleOpenFilterSidebarItem}
      cancelFilterSidebar={handleCancelFilterSidebar}
    />
  );

  return (
    <>
      <Header title={t('queue.queues')} testId={TITLE_TAG} />

      <Panel>
        <Stack direction="horizontal" spacing={15} className="w-full">
          <QueuesHeaderSearchForm onSubmit={onFilterSubmit} />
          {userHasPermissions([Permissions.CreateQueue]) && (
            <Button
              isSelected={isSidebarOpen && !queueToEdit && !isFilterSidebarOpen}
              testId={ADD_QUEUE_BUTTON_TAG}
              onClick={handlePrepareCreateQueue}
            >
              <PlusIcon />
              {t('queue.add')}
            </Button>
          )}
          <ActiveFilters />
          <Button
            isSelected={isFilterSidebarOpen}
            className="mis-auto"
            testId={TOGGLE_FILTER_BUTTON_TAG}
            onClick={toggleFilterSidebar}
          >
            <FilterIcon />
            {t('filter.filters')}
          </Button>
        </Stack>
      </Panel>

      <Content testId={CONTAINER_TAG}>
        {(loading || mutationInProgress) && <Progress withOverlay />}

        {!skipRendering && queues.length > 0 && (
          <>
            <QueuesTable
              queuesResult={queuesResult}
              acdQueuesResult={acdQueuesResult}
              skillsResult={skillsResult}
              onAssignQueue={handlePrepareAssignQueue}
              onEditQueue={handlePrepareEditQueue}
              onDeleteQueue={handleShowDeleteModal}
              activeId={queueToEdit ? queueToEdit.id : undefined}
            />
            <ModalConfirmation
              title={t('queue.deleteQueue')}
              acceptCaption={t('layout.action.delete')}
              cancelCaption={t('layout.action.cancel')}
              onAccept={handleDelete}
              onCancel={handleCloseDeleteModal}
              isOpen={!!queueToDelete}
            >
              <Trans i18nKey="queue.modal.deleteQueueBody" values={{ name: queueToDelete?.name }}>
                Do you really want to delete chosen queue <em>{queueToDelete ? queueToDelete.name : undefined}</em>?
              </Trans>
            </ModalConfirmation>
            <ModalConfirmation
              isOpen={forceEditModal.open}
              acceptCaption={t('layout.action.proceed')}
              cancelCaption={t('layout.action.cancel')}
              title={t('layout.warning')}
              onAccept={handleForceEditModalSubmit}
              onCancel={handleForceEditModalClose}
            >
              {t('queue.forcePushModalBody')}
            </ModalConfirmation>
            <ModalConfirmation
              isOpen={usedAgentsModal.state === 'open'}
              acceptCaption={t('layout.action.proceed')}
              cancelCaption={t('layout.action.cancel')}
              title={t('layout.warning')}
              onAccept={handleUsedAgentsModalSubmit}
              onCancel={handleUsedAgentsModalClose}
            >
              {usedAgentsModal.state === 'open' && (
                <Stack direction="vertical">
                  <p>{t('queue.modal.usedAgentsBody')}</p>
                  <p>{t('queue.modal.usedAgentsBody2', { count: usedAgentsModal.agents.length })}</p>
                  <ul>
                    {usedAgentsModal.agents.map((agent) => (
                      <li key={agent.id}>{fullNameOfAgent(agent)}</li>
                    ))}
                  </ul>
                </Stack>
              )}
            </ModalConfirmation>
          </>
        )}

        {!skipRendering && queues.length === 0 && (
          <Center>
            <IllustratedMessage
              heading={
                isFilterActive || !equals(defaultQueueSidebarFilter, sidebarFilter)
                  ? t('filter.noResultsMatch')
                  : t('queue.noEntries')
              }
              icon={<SearchNoneIcon />}
            />
          </Center>
        )}
      </Content>

      <SidebarWrapper>
        {isSidebarOpen && isFilterSidebarOpen && (
          <QueuesFilterSidebar
            acdQueuesResult={acdQueuesResult}
            handleCancel={handleCancelFilterSidebar}
            preOpenedItemIds={preOpenedItemIds}
            setPreOpenedItemIds={setPreOpenedItemIds}
            setSidebarFilter={setSidebarFilter}
            sidebarFilter={sidebarFilter}
            skillsResult={skillsResult}
            timezoneUsedInQueuesResult={timezoneUsedInQueuesResult}
          />
        )}
        {isSidebarOpen && !isFilterSidebarOpen && (
          <QueuesSidebar
            acdQueuesResult={acdQueuesResult}
            handleCancel={handleCancel}
            handleSubmit={handleSubmit}
            queue={queueToEdit}
            skillsResult={skillsResult}
            t={t}
            timezonesResult={timezonesResult}
          />
        )}
      </SidebarWrapper>
    </>
  );
};
